import { toLocalGameDate } from "@workspace/shared";
import { MAX_UNIT } from "../../../lib/playerUnit.ts";

/**
 * The arithmetic behind the grading apps' Unit and Detail reports (client
 * 2026-10-20), kept apart from the pages so it can be checked on its own.
 *
 * The report's units are the organization's grading apps — Linebackers,
 * Defensive Backs, Wide Receivers… — each graded by its own coach in its own
 * app. A practice is a day: every unit's practice graded for it.
 *
 * A play grades up to six players (the app's Unit): slot n holds the player
 * (jerseyNumber{n}), the grade ("+" / "-", grade{n}), and what the player
 * did — Production values (prod{n}) and Correctables (corr{n}).
 *
 * - A rep is a graded slot (+ or -). Grade = pluses ÷ reps.
 * - A tag counts where the unit's Settings lists it now: a value in the
 *   Correctables column counts as a correctable even on plays tagged before
 *   that column existed (when it sat in Production), so moving a value
 *   between the two columns re-files its whole history. A value in neither
 *   column stays where it was tagged.
 * - Capitals aside: Settings now saves typed values uppercased (in-game's
 *   rule), so a play from before says "Loaf" where Settings says "LOAF" —
 *   the same value, shown as Settings spells it.
 */

export type GradingRow = Record<string, unknown>;

/** A unit's Settings, as the reports read them. */
export interface GradingSettings {
  /** The Production column's values, in Settings order. */
  production: string[];
  /** The Correctables column's values, in Settings order. */
  correctables: string[];
  /** The Period header's values, in Settings order — how the Summary orders a practice's periods. */
  periods?: string[];
}

type FieldLike = { name?: unknown; label?: unknown; values?: unknown };

/** A Settings column's values: trimmed, each once (capitals aside). */
const valuesOf = (field: FieldLike | undefined): string[] => {
  const seen = new Set<string>();
  return (Array.isArray(field?.values) ? (field.values as unknown[]) : [])
    .map((v) => (typeof v === "string" ? v : (v as { value?: unknown } | null)?.value))
    .filter((v): v is string => typeof v === "string" && v.trim() !== "")
    .map((v) => v.trim())
    .filter((v) => !seen.has(v.toUpperCase()) && Boolean(seen.add(v.toUpperCase())));
};

/** The report settings, read from a unit's Settings fields. */
export function readGradingSettings(fields: readonly FieldLike[] | null | undefined): GradingSettings {
  const byName = (name: string) => (fields ?? []).find((f) => f?.name === name);
  const correctables = valuesOf(byName("corr1"));
  // A value in both columns counts as a correctable: no Production column for it.
  const isCorrectable = new Set(correctables.map((v) => v.toUpperCase()));
  return {
    production: valuesOf(byName("prod1")).filter((v) => !isCorrectable.has(v.toUpperCase())),
    correctables,
  };
}

/**
 * The Period header's values, in Settings order — not its placeholder
 * ("Period", the header's own name, which Play Entry stores as no period).
 */
export const periodValues = (headers: readonly FieldLike[] | null | undefined): string[] =>
  valuesOf((headers ?? []).find((h) => h?.name === "period")).filter((v) => !isPeriodPlaceholder(v));

/** "Period" stored as a play's period means none was picked. */
export const isPeriodPlaceholder = (value: string): boolean => value.trim().toUpperCase() === "PERIOD";

/** A multi-value cell: a comma string or an array (Production, Correctables). */
export function parseValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

export interface Tally {
  /** Graded reps (+ or -). */
  reps: number;
  plus: number;
  minus: number;
  production: Record<string, number>;
  correctables: Record<string, number>;
  /**
   * Graded reps with at least one Production tag, and with at least one
   * Correctable, by the unit's Settings: the Detail's shares of reps (a rep
   * often carries several tags, so tags ÷ reps passed 100%; client
   * 2026-11-02). Null when a practice came from an API too old to say.
   */
  taggedReps: { production: number; correctables: number } | null;
}

/**
 * One player's counts in one practice, each tag under the column it was
 * tagged in — what the API's grading report sends for a saved practice, and
 * what `tallyPlayers` counts for the one on this iPad.
 */
export interface RawTally {
  reps: number;
  plus: number;
  minus: number;
  production: Record<string, number>;
  correctables: Record<string, number>;
  /**
   * Graded reps with any tag, by what they were tagged with: the key a JSON
   * [Production values, Correctables values]. Missing from an older API.
   */
  tagged?: Record<string, number>;
}

/**
 * Counts by value, for any value a coach typed — "constructor" or
 * "__proto__" included: a record with no prototype to collide with.
 */
const counts = (): Record<string, number> => Object.create(null) as Record<string, number>;

export const emptyTally = (): Tally => ({
  reps: 0,
  plus: 0,
  minus: 0,
  production: counts(),
  correctables: counts(),
  taggedReps: { production: 0, correctables: 0 },
});

const emptyRawTally = (): Required<RawTally> => ({
  reps: 0,
  plus: 0,
  minus: 0,
  production: counts(),
  correctables: counts(),
  tagged: counts(),
});

/** Adds `from` into `into` (mutates `into`; returns it). */
export function addTally(into: Tally, from: Tally): Tally {
  into.reps += from.reps;
  into.plus += from.plus;
  into.minus += from.minus;
  for (const [k, v] of Object.entries(from.production)) into.production[k] = (into.production[k] ?? 0) + v;
  for (const [k, v] of Object.entries(from.correctables)) into.correctables[k] = (into.correctables[k] ?? 0) + v;
  // Unknown in any part, unknown in the whole.
  into.taggedReps =
    into.taggedReps && from.taggedReps
      ? {
          production: into.taggedReps.production + from.taggedReps.production,
          correctables: into.taggedReps.correctables + from.taggedReps.correctables,
        }
      : null;
  return into;
}

export const sumTallies = (tallies: Iterable<Tally>): Tally => {
  const total = emptyTally();
  for (const t of tallies) addTally(total, t);
  return total;
};

/** Players' tallies added together, player by player. */
export function mergePlayers(groups: Iterable<ReadonlyMap<string, Tally>>): Map<string, Tally> {
  const out = new Map<string, Tally>();
  for (const players of groups) {
    for (const [player, t] of players) {
      let into = out.get(player);
      if (!into) out.set(player, (into = emptyTally()));
      addTally(into, t);
    }
  }
  return out;
}

export const countTags = (record: Record<string, number>): number =>
  Object.values(record).reduce((a, b) => a + b, 0);

/**
 * A single-value cell (the player): a string as it is — "22 Durand, Duke" is
 * one player — or each value of a multi-select.
 */
const singleValues = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : typeof value === "string" || typeof value === "number"
      ? [String(value).trim()].filter(Boolean)
      : [];

const count = (into: Record<string, number>, values: string[]) => {
  for (const v of values) into[v] = (into[v] ?? 0) + 1;
};

/**
 * One practice's plays counted per player, and per period for the whole unit
 * — the API's grading report rules (API src/game/grading-report.ts; the two
 * share their tests' examples), for the practice on this iPad, which the
 * server may not have yet. Its day is the one most of its plays were entered
 * for; a play with no period counts under "".
 */
export function tallyPlayers(rows: readonly GradingRow[], slotCount = MAX_UNIT): {
  practiceDate: string | null;
  players: Map<string, Required<RawTally>>;
  periods: Map<string, Required<RawTally>>;
} {
  const players = new Map<string, Required<RawTally>>();
  const periods = new Map<string, Required<RawTally>>();
  const days = new Map<string, number>();
  for (const row of rows) {
    if (!row || row.isDeleted === true) continue;
    const day = typeof row.practiceDate === "string" ? row.practiceDate.trim() : "";
    if (day) days.set(day, (days.get(day) ?? 0) + 1);
    const period = singleValues(row.period)[0] ?? "";
    for (let n = 1; n <= slotCount; n++) {
      const player = singleValues(row[`jerseyNumber${n}`])[0];
      if (!player) continue;
      let t = players.get(player);
      if (!t) players.set(player, (t = emptyRawTally()));
      let p = periods.get(period);
      if (!p) periods.set(period, (p = emptyRawTally()));
      const grade = row[`grade${n}`];
      const production = parseValues(row[`prod${n}`]);
      const correctables = parseValues(row[`corr${n}`]);
      // A graded rep, by all it was tagged with (the API's key).
      const tags =
        (grade === "+" || grade === "-") && production.length + correctables.length > 0
          ? JSON.stringify([[...production].sort(), [...correctables].sort()])
          : null;
      for (const into of [t, p]) {
        if (grade === "+") {
          into.reps += 1;
          into.plus += 1;
        } else if (grade === "-") {
          into.reps += 1;
          into.minus += 1;
        }
        count(into.production, production);
        count(into.correctables, correctables);
        if (tags) count(into.tagged, [tags]);
      }
    }
  }
  // The most common day; of equals the earliest, whatever order the plays
  // come in (the API's rule).
  let practiceDate: string | null = null;
  let most = 0;
  for (const [day, n] of days) {
    if (n > most || (n === most && practiceDate !== null && day < practiceDate)) {
      practiceDate = day;
      most = n;
    }
  }
  return { practiceDate, players, periods };
}

/** Settings' spelling of each value, by its capitals. */
const spellings = (values: readonly string[]) => new Map(values.map((v) => [v.toUpperCase(), v]));

/**
 * A player's counts with each tag where the unit's Settings lists it now
 * (Correctables first); a value in neither column stays where it was tagged.
 */
export function classifyTally(raw: RawTally, settings: GradingSettings): Tally {
  const production = spellings(settings.production);
  const correctables = spellings(settings.correctables);
  const out: Tally = { ...emptyTally(), reps: raw.reps, plus: raw.plus, minus: raw.minus };
  /** Where a value counts: where Settings lists it (Correctables first), else where it was tagged. */
  const columnOf = (key: string, taggedAs: "production" | "correctables") => {
    const correctable = correctables.get(key);
    const produced = production.get(key);
    return correctable
      ? (["correctables", correctable] as const)
      : produced
        ? (["production", produced] as const)
        : ([taggedAs, key] as const);
  };
  const place = (value: string, n: number, taggedAs: "production" | "correctables") => {
    const key = value.trim().toUpperCase();
    if (!key || !(n > 0)) return;
    const [column, name] = columnOf(key, taggedAs);
    out[column][name] = (out[column][name] ?? 0) + n;
  };
  for (const [v, n] of Object.entries(raw.production ?? {})) place(v, n, "production");
  for (const [v, n] of Object.entries(raw.correctables ?? {})) place(v, n, "correctables");

  // The reps with a Production tag, and with a Correctable, each tag where Settings puts it.
  if (!raw.tagged) out.taggedReps = null;
  else {
    for (const [key, n] of Object.entries(raw.tagged)) {
      let lists: unknown;
      try {
        lists = JSON.parse(key);
      } catch {
        continue;
      }
      if (!Array.isArray(lists) || !(n > 0)) continue;
      const columns = new Set<string>();
      (["production", "correctables"] as const).forEach((taggedAs, i) => {
        for (const v of Array.isArray(lists[i]) ? lists[i] : []) {
          const value = String(v).trim().toUpperCase();
          if (value) columns.add(columnOf(value, taggedAs)[0]);
        }
      });
      if (columns.has("production")) out.taggedReps!.production += n;
      if (columns.has("correctables")) out.taggedReps!.correctables += n;
    }
  }
  return out;
}

/** Pluses ÷ reps, as a percentage; null with no graded reps. */
export const gradePct = (t: Pick<Tally, "reps" | "plus">): number | null =>
  t.reps > 0 ? (t.plus / t.reps) * 100 : null;

/** Reps ÷ reps, as a percentage (the reps with a tag, of all): null with no graded reps. */
export const perRepPct = (tagged: number, reps: number): number | null =>
  reps > 0 ? (tagged / reps) * 100 : null;

/** "1 rep", "12 reps". */
export const repsLabel = (reps: number): string => `${reps} ${reps === 1 ? "rep" : "reps"}`;

/** Shown to one decimal; the colour follows the number shown. */
const shown = (pct: number) => Math.round(pct * 10) / 10;

export const formatPct = (pct: number | null): string => (pct == null ? "—" : `${shown(pct).toFixed(1)}%`);

export type GradeTone = "good" | "ok" | "low";

/** 95%+ good, 80 up to 95 ok, under 80 low. */
export const gradeTone = (pct: number | null): GradeTone | null => {
  if (pct == null) return null;
  const v = shown(pct);
  return v >= 95 ? "good" : v >= 80 ? "ok" : "low";
};

/**
 * A Jersey # value as a number and a name: "22 Duke Durand" → 22, Duke
 * Durand; "22" → 22, no name; "Duke" → no number, Duke.
 */
export function parseJersey(value: string): { number: string; name: string } {
  const match = /^\s*#?\s*(\d{1,3})(?:\s*[-–—.:,]\s*|\s+|$)(.*)$/.exec(value);
  if (match) return { number: match[1] ?? "", name: (match[2] ?? "").trim() };
  return { number: "", name: value.trim() };
}

/** Best grade first, then most reps, then by number. */
export const byGrade = ([a, ta]: [string, Tally], [b, tb]: [string, Tally]): number =>
  (gradePct(tb) ?? -1) - (gradePct(ta) ?? -1) ||
  tb.reps - ta.reps ||
  Number(parseJersey(a).number || 999) - Number(parseJersey(b).number || 999) ||
  a.localeCompare(b);

/**
 * A table's tag columns: every value in its Settings column (in order), then
 * any other value tagged in these tallies (A–Z) — nothing tagged is dropped.
 */
export function tagColumns(settingsValues: readonly string[], counts: Iterable<Record<string, number>>): string[] {
  const listed = new Set(settingsValues);
  const extra = new Set<string>();
  for (const record of counts) {
    for (const [k, v] of Object.entries(record)) if (v > 0 && !listed.has(k)) extra.add(k);
  }
  return [...settingsValues, ...[...extra].sort((a, b) => a.localeCompare(b))];
}

/* ── Days ── */

const pad = (n: number) => String(n).padStart(2, "0");

/** A date (an ISO day or a saved instant) as its local day, "2026-10-01". */
export function localDay(date: string | null | undefined): string | null {
  const d = toLocalGameDate(date ?? null);
  return d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : null;
}

/** Days between two local days (b − a). */
export const daysBetween = (a: string, b: string): number => {
  const utc = (day: string) => {
    const [y, m, d] = day.split("-").map(Number);
    return Date.UTC(y ?? 0, (m ?? 1) - 1, d ?? 1);
  };
  return Math.round((utc(b) - utc(a)) / 86_400_000);
};

/** The season a practice belongs to: its (local) calendar year. */
export const seasonOf = (date: string | null): number | null => toLocalGameDate(date)?.getFullYear() ?? null;

/** "TUE" and "SEP 29" for a practice's day column. */
export function dayLabel(date: string | null): { weekday: string; day: string } {
  const d = toLocalGameDate(date);
  if (!d) return { weekday: "", day: "" };
  return {
    weekday: d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
    // "SEPT", as every other date in the reports spells it.
    day: `${d.getMonth() === 8 ? "Sept" : d.toLocaleDateString("en-US", { month: "short" })} ${d.getDate()}`.toUpperCase(),
  };
}

/** "Tuesday, Sept 8, 2026" (the Unit report's practice). */
export function longDate(date: string | null): string {
  const d = toLocalGameDate(date);
  if (!d) return "";
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const short = month === "September" ? "Sept" : month.length > 4 ? month.slice(0, 3) : month;
  return `${d.toLocaleDateString("en-US", { weekday: "long" })}, ${short} ${d.getDate()}, ${d.getFullYear()}`;
}

/** "Tue, Sept 29" (a practice in a picker). */
export function shortDate(date: string | null): string {
  const full = longDate(date);
  if (!full) return "";
  const [weekday, rest] = [full.slice(0, 3), full.split(", ")[1] ?? ""];
  return `${weekday}, ${rest}`;
}
